/**
 * Shared (de)serialization for `professionalSituation` between the flat form shape
 * and the nested canonical payload persisted to the onboarding API.
 */
import { normalizeProSituationStatus } from '@/utils/common-utils';

/** Loose record used while flattening or serializing professional-situation payloads. */
type AnyRecord = Record<string, any>;
/** Which form owns the professional-situation buckets (field sets differ). */
export type ProfessionalSituationPersona = 'tutor' | 'parent';

/** True when a remote tutor professional situation must collect valued engagement. */
export function isRemoteValuedEngagementRequired(status?: string): boolean {
  return Boolean(status);
}

/** True when a value is present (not undefined, null, or empty string). */
function isFilled(v: unknown): boolean {
  return v !== undefined && v !== null && v !== '';
}

/** Drops undefined/null/'' entries so we never persist empty keys. */
function clean(obj: AnyRecord): AnyRecord {
  const out: AnyRecord = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

/** Coerce a string or number id to a string; other types become empty. */
function asIdString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/** Extracts a department code string from either a string or a `{ departmentId }` object. */
function departmentIdOf(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'object') return asIdString((v as AnyRecord).departmentId);
  return asIdString(v);
}

/** Builds a `{ departmentId, departmentName }` ref from a department code string (or passes an object through). */
function toDepartmentRef(v: unknown): AnyRecord | undefined {
  if (!isFilled(v)) return undefined;
  if (typeof v === 'object') return v as AnyRecord;
  const id = asIdString(v);
  if (!id) return undefined;
  return { departmentId: id, departmentName: '' };
}

/** Builds the `[{ establishmentId, establishmentName }]` list from the flat establishment fields. */
function establishmentEntry(flat: AnyRecord): AnyRecord[] | undefined {
  if (!isFilled(flat.establishment) && !isFilled(flat.establishmentName)) return undefined;
  return [
    {
      establishmentId: flat.establishment ?? '',
      establishmentName: flat.establishmentName ?? '',
    },
  ];
}

/** Build the nested `company` list from flat employer / company fields. */
function companyEntry(flat: AnyRecord): AnyRecord[] | undefined {
  if (!isFilled(flat.employerName || flat.companyName)) return undefined;
  return [
    {
      employerId: flat.employerId ?? '',
      employerName: flat.employerName ?? flat.companyName,
      establishmentId: flat.employerId ?? '',
      establishmentName: flat.employerName ?? flat.companyName,
    },
  ];
}

/**
 * Converts the flat form professionalSituation into the nested canonical payload
 * stored in the onboarding sections (compatible with the mobile app + detail views).
 */
export function serializeProfessionalSituation(
  flat: AnyRecord | undefined,
  persona: ProfessionalSituationPersona,
): AnyRecord | undefined {
  if (!flat || typeof flat !== 'object' || !flat.status) return flat;

  const status = normalizeProSituationStatus(flat.status as string);
  const payload: AnyRecord = { status };
  const isTutor = persona === 'tutor';

  switch (status) {
    case 'high_school_student':
      payload.high_school_student = clean({
        schoolEstablishment: establishmentEntry(flat),
        studyLevel: flat.level,
        schoolCurriculum: flat.curriculum,
        associativeEngagementValued: flat.associativeEngagement,
      });
      break;

    case 'student':
      payload.student = clean({
        higherEducationEstablishment: establishmentEntry(flat),
        studyLevel: flat.level,
        associativeEngagementValued: flat.associativeEngagement,
        ufrCurriculum: isFilled(flat.studentCurriculum) ? [flat.studentCurriculum] : undefined,
        areaOfStudy: flat.curriculumDetail,
      });
      break;

    case 'employee':
    case 'entrepreneur':
      // Each status is stored in its own bucket so mobile (which reads source[status]) and
      // the web detail view both resolve the data correctly.
      payload[status] = clean(
        isTutor
          ? {
              fieldOfActivity: flat.fieldOfActivity,
              company: companyEntry(flat),
              activityDepartment: toDepartmentRef(flat.activityDepartment),
              isCompanyPartner: flat.isCompanyPartner,
              isEngagementValorised: flat.isEngagementValorised,
              associativeEngagementValued: flat.associativeEngagement,
            }
          : {
              fieldOfActivity: flat.fieldOfActivity,
              specifiedActivityField: flat.specifiedActivityField,
              company: companyEntry(flat),
              activityDepartment: toDepartmentRef(flat.activityDepartment),
            },
      );
      break;

    case 'civil_servant':
      payload.civil_servant = clean({
        fieldOfActivity: flat.fieldOfActivity,
        company: companyEntry(flat),
        activityDepartment: toDepartmentRef(flat.activityDepartment),
        civilServantBranch: flat.civilServantBranch,
        ...(isTutor
          ? {
              isCompanyPartner: flat.isCompanyPartner,
              isEngagementValorised: flat.isEngagementValorised,
              associativeEngagementValued: flat.associativeEngagement,
            }
          : {}),
      });
      break;

    case 'unemployed':
      payload.unemployed = clean({
        fieldOfActivity: flat.fieldOfActivity,
        ...(isTutor ? { associativeEngagementValued: flat.associativeEngagement } : {}),
      });
      break;

    case 'retired':
      payload.retired = clean({
        fieldOfActivity: flat.fieldOfActivity,
        ...(isTutor ? { associativeEngagementValued: flat.associativeEngagement } : {}),
      });
      break;

    case 'executive':
    case 'intermediate':
    case 'worker':
    case 'farmer':
    case 'other':
      payload[status] = isTutor
        ? clean({ associativeEngagementValued: flat.associativeEngagement })
        : {};
      break;
  }

  return payload;
}

/** Copy `value` onto `out[key]` only when the target is still empty. */
function fillIfEmpty(out: AnyRecord, key: string, value: unknown) {
  if (isFilled(value) && !isFilled(out[key])) out[key] = value;
}

/** Nested status bucket from a persisted professionalSituation payload. */
function resolveSituationBucket(
  ps: AnyRecord,
  rawStatus: string | undefined,
  status: string,
): AnyRecord | undefined {
  let bucket = rawStatus ? (ps[rawStatus] as AnyRecord | undefined) : undefined;
  if (!bucket) bucket = ps[status] as AnyRecord | undefined;
  if (!bucket && (status === 'entrepreneur' || status === 'civil_servant')) {
    bucket = ps.employee as AnyRecord | undefined;
  }
  return bucket;
}

/** Flatten school / higher-education bucket fields onto the form shape. */
function flattenStudentFields(out: AnyRecord, bucket: AnyRecord, status: string) {
  const estList = bucket.schoolEstablishment ?? bucket.higherEducationEstablishment;
  const est = Array.isArray(estList) ? estList[0] : undefined;
  if (est) {
    if (!isFilled(out.establishment)) out.establishment = est.establishmentId ?? '';
    if (!isFilled(out.establishmentName)) out.establishmentName = est.establishmentName ?? '';
  }
  fillIfEmpty(out, 'level', bucket.studyLevel);
  fillIfEmpty(out, 'associativeEngagement', bucket.associativeEngagementValued);
  if (status === 'high_school_student') {
    fillIfEmpty(out, 'curriculum', bucket.schoolCurriculum);
    return;
  }
  if (
    Array.isArray(bucket.ufrCurriculum) &&
    bucket.ufrCurriculum.length > 0 &&
    !isFilled(out.studentCurriculum)
  ) {
    out.studentCurriculum = bucket.ufrCurriculum[0];
  }
  fillIfEmpty(out, 'curriculumDetail', bucket.areaOfStudy);
}

/** Flatten employee / entrepreneur / civil-servant bucket fields onto the form shape. */
function flattenWorkerFields(out: AnyRecord, bucket: AnyRecord) {
  if (isFilled(bucket.fieldOfActivity) && !isFilled(out.fieldOfActivity)) {
    out.fieldOfActivity = bucket.fieldOfActivity;
  } else if (isFilled(bucket.activitySector) && !isFilled(out.fieldOfActivity)) {
    out.fieldOfActivity = bucket.activitySector;
  }
  fillIfEmpty(out, 'specifiedActivityField', bucket.specifiedActivityField);
  const company = Array.isArray(bucket.company) ? bucket.company[0] : undefined;
  if (!isFilled(out.employerId)) {
    out.employerId = company?.employerId ?? company?.establishmentId ?? '';
  }
  if (!isFilled(out.employerName)) {
    out.employerName = company?.employerName ?? company?.establishmentName ?? '';
  }
  if (!isFilled(out.companyName)) {
    out.companyName = out.employerName || bucket.companyName || '';
  }
  if (isFilled(bucket.activityDepartment) && !isFilled(out.activityDepartment)) {
    out.activityDepartment = departmentIdOf(bucket.activityDepartment);
  }
  if (bucket.isCompanyPartner != null && out.isCompanyPartner == null) {
    out.isCompanyPartner = bucket.isCompanyPartner;
  }
  if (bucket.isEngagementValorised != null && out.isEngagementValorised == null) {
    out.isEngagementValorised = bucket.isEngagementValorised;
  }
  fillIfEmpty(out, 'associativeEngagement', bucket.associativeEngagementValued);
  fillIfEmpty(out, 'civilServantBranch', bucket.civilServantBranch);
}

/**
 * Converts a persisted professionalSituation (nested canonical OR already-flat) into the flat
 * shape consumed by {@link ProfessionalSituationFields} and the detail renderers. Idempotent:
 * existing flat values are preserved and only missing ones are filled from the nested bucket.
 */
export function flattenProfessionalSituation(ps: AnyRecord | undefined): AnyRecord | undefined {
  if (!ps || typeof ps !== 'object') return ps;

  const rawStatus = ps.status as string | undefined;
  const status = normalizeProSituationStatus(rawStatus);
  const out: AnyRecord = { ...ps, status };
  const bucket = resolveSituationBucket(ps, rawStatus, status);

  if (bucket && typeof bucket === 'object') {
    if (status === 'high_school_student' || status === 'student') {
      flattenStudentFields(out, bucket, status);
    } else {
      flattenWorkerFields(out, bucket);
    }
  }

  if (out.activityDepartment && typeof out.activityDepartment === 'object') {
    out.activityDepartment = departmentIdOf(out.activityDepartment);
  }

  return out;
}
